import { Injectable, signal } from '@angular/core';

import {
  COOKIE_CONSENT_COOKIE_NAME,
  DEFAULT_COOKIE_PREFERENCES,
} from '../../shared/constants/cookie-consent.constants';
import { CookiePreferences } from '../../shared/interfaces/cookie-preferences.interface';

export type CookieConsentDialog = 'policy' | 'settings' | null;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  public readonly preferences = signal<CookiePreferences>(this.readPreferences());
  public readonly hasSavedPreferences = signal(
    document.cookie.includes(`${COOKIE_CONSENT_COOKIE_NAME}=`),
  );
  public readonly dialog = signal<CookieConsentDialog>(null);

  public openSettings(): void {
    this.dialog.set('settings');
  }

  public openPolicy(): void {
    this.dialog.set('policy');
  }

  public closeDialog(): void {
    this.dialog.set(null);
  }

  public save(preferences: CookiePreferences): void {
    const value = encodeURIComponent(JSON.stringify(preferences));

    document.cookie = `${COOKIE_CONSENT_COOKIE_NAME}=${value}; Max-Age=31536000; Path=/; SameSite=Lax`;
    this.preferences.set(preferences);
    this.hasSavedPreferences.set(true);
  }

  public acceptAll(): void {
    this.save({ necessary: true, performance: true, functional: true, targeting: true });
  }

  private readPreferences(): CookiePreferences {
    const cookie = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${COOKIE_CONSENT_COOKIE_NAME}=`));

    if (!cookie) {
      return DEFAULT_COOKIE_PREFERENCES;
    }

    try {
      const stored = JSON.parse(
        decodeURIComponent(cookie.slice(COOKIE_CONSENT_COOKIE_NAME.length + 1)),
      );

      return {
        necessary: true,
        performance: Boolean(stored.performance),
        functional: Boolean(stored.functional),
        targeting: Boolean(stored.targeting),
      };
    } catch {
      return DEFAULT_COOKIE_PREFERENCES;
    }
  }
}
